"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var throttle = require('lodash.throttle');
var ACTIVE_EVENTS = [
    'scroll',
    'resize',
    'wheel',
    'keydown',
    'keyup',
    'click',
    'contextmenu',
    'mousemove',
    'touchstart',
    'touchend',
    'touchmove',
];
var LISTENERS_THROTTLE_TIMEOUT = 1000;
/**
 * @description Компонент WindowInteraction ответственен за события взаимодействия пользователя со страницей.
 * Пользователь может быть либо активным - двигать мышкой, нажимать по клаве, кликать.
 * Либо неактивным - когда пользователь бездействует <inactiveTime> миллисекунд.
 * Страница может быть либо видимой - когда пользователь находится на текущей вкладке.
 * Либо скрытой - когда пользователь перешел на другую вкладку, либо свернул браузер.
 * Важно различать эти 2 параметра - активность пользователя (this.active) и видимость вкладки (document.hidden).
 * Если вкладка видима - пользователь может быть либо активным, либо неактивным.
 * Если вкладка скрыта - пользователь может быть только неактивным.
 * Вызов колбэков должен быть линейным:
 * onVisible -> onInactive -> onActive -> onInactive -> ... -> onHidden
 * либо
 * onVisible -> onHidden -> onVisible -> onHidden
 * либо
 * onVisible -> onActive -> onHidden
 * либо
 * onVisible -> onInactive -> onHidden
 * Другие схемы не допускаются, поскольку усложняют взаимодействие с данным компонентом.
 * Вызов колбэков onInactive и onActive должен быть только в те моменты, когда страница видима.
 * Они не должны вызываться после колбэка onHidden.
 * Также колбэки не должны дублироваться: onVisible -> onVisible или onActive -> onActive и т.д.
 */
var WindowInteraction = /** @class */ (function () {
    function WindowInteraction(_a) {
        var _this = this;
        var inactiveTime = _a.inactiveTime, onInactive = _a.onInactive, onActive = _a.onActive, onVisible = _a.onVisible, onHidden = _a.onHidden;
        this.handleWindowOpen = function () {
            _this.windowOpen = true;
            // если при первой загрузке пользователь находится на текущей вкладке - меняем состояние на активное
            _this.active = !document.hidden;
            if (_this.active) {
                _this.setInactiveTimeout();
                _this.onVisible();
            }
        };
        this.handleWindowClose = function () {
            // если документ скрыт - onHidden уже вызывался в handleVisibilityChange
            if (document.hidden || _this.windowWillClose) {
                return;
            }
            _this.windowWillClose = true;
            _this.active = false;
            _this.clearInactiveTimeout();
            _this.onHidden();
        };
        this.handleActive = throttle(function () {
            if (!_this.windowOpen || document.hidden || _this.windowWillClose) {
                return;
            }
            // откладываем переход в неактивное состояние пока пользователь взаимодействует со страницей
            _this.setInactiveTimeout();
            if (!_this.active) {
                _this.active = true;
                _this.onActive();
            }
        }, LISTENERS_THROTTLE_TIMEOUT, { leading: true, trailing: false });
        this.handleVisibilityChange = function () {
            if (!_this.windowOpen || _this.windowWillClose) {
                return;
            }
            if (document.hidden) {
                _this.active = false;
                _this.clearInactiveTimeout();
                _this.onHidden();
            }
            else {
                _this.active = true;
                _this.setInactiveTimeout();
                _this.onVisible();
            }
        };
        this.windowWillClose = false;
        this.active = false;
        this.inactiveTime = inactiveTime;
        this.inactiveTimerId = null;
        this.onActive = onActive;
        this.onInactive = onInactive;
        this.onVisible = onVisible;
        this.onHidden = onHidden;
    }
    WindowInteraction.prototype.start = function () {
        var _this = this;
        if (document.readyState === 'interactive' || document.readyState === 'complete') {
            this.handleWindowOpen();
        }
        else {
            document.addEventListener('DOMContentLoaded', this.handleWindowOpen, { capture: true, passive: true });
        }
        /**
         * "capture: true" нужен по двум причинам:
         * 1) некоторые события, используемые здесь, не всплывают (например, resize)
         * 2) стадия захвата гаранирует вызов слушателей.
         * Поскольку на стадии всплытия слушатели элементов, находящиеся ниже в DOM-дереве, могут остановить распространение события.
         *
         * В некоторых браузерах при закрытии не срабатывает pagehide, поэтому нужен beforeunload.
         */
        document.addEventListener('visibilitychange', this.handleVisibilityChange, { capture: true, passive: true });
        ACTIVE_EVENTS.forEach(function (event) { return window.addEventListener(event, _this.handleActive, { capture: true, passive: true }); });
        window.addEventListener('beforeunload', this.handleWindowClose, { capture: true, passive: true });
        window.addEventListener('pagehide', this.handleWindowClose, { capture: true, passive: true });
    };
    WindowInteraction.prototype.stop = function () {
        var _this = this;
        this.active = false;
        this.windowWillClose = false;
        this.clearInactiveTimeout();
        document.removeEventListener('DOMContentLoaded', this.handleWindowOpen, { capture: true });
        document.removeEventListener('visibilitychange', this.handleVisibilityChange, { capture: true });
        ACTIVE_EVENTS.forEach(function (event) { return window.removeEventListener(event, _this.handleActive, { capture: true }); });
        window.removeEventListener('beforeunload', this.handleWindowClose, { capture: true });
        window.removeEventListener('pagehide', this.handleWindowClose, { capture: true });
    };
    /**
     * @description Ставит таймер, который меняет состояние на неактивное, если пользователь не активен <inactiveTime> миллисекунд
     */
    WindowInteraction.prototype.setInactiveTimeout = function () {
        var _this = this;
        this.clearInactiveTimeout();
        this.inactiveTimerId = window.setTimeout(function () {
            if (!_this.active || document.hidden || _this.windowWillClose) {
                return;
            }
            _this.active = false;
            _this.onInactive();
        }, this.inactiveTime);
    };
    WindowInteraction.prototype.clearInactiveTimeout = function () {
        if (this.inactiveTimerId) {
            clearTimeout(this.inactiveTimerId);
            this.inactiveTimerId = null;
        }
    };
    return WindowInteraction;
}());
exports.WindowInteraction = WindowInteraction;
//# sourceMappingURL=windowInteraction.js.map